import { createClient } from "@supabase/supabase-js";
import type {
  Product,
  Program,
  GeneratedContent,
  QAItem,
  TeamMember,
  AppSettings,
  ProductActivity,
  ActivityAction,
} from "./types";
import { CONFIG, PXM_DEBUG } from "./constants";

// ─── Debug Logging ────────────────────────────────────────────
export const log = (...args: any[]) => {
  if (PXM_DEBUG) console.log("[PXM]", ...args);
};

export const warn = (...args: any[]) => {
  if (PXM_DEBUG) console.warn("[PXM]", ...args);
};

// ─── Supabase Config ──────────────────────────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_KEY || "";

export const supabase = SUPABASE_URL && SUPABASE_KEY ? createClient(SUPABASE_URL, SUPABASE_KEY) : null;

// ─── Column Mappings ─────────────────────────────────────────
// Known DB columns per table — strip anything not in this list to prevent Supabase errors
const DB_COLUMNS: Record<string, string[]> = {
  products: [
    "id",
    "item_number",
    "title",
    "hero_image",
    "selling_features",
    "completion",
    "program_id",
    "dimensions",
    "weight",
    "material",
    "color",
    "pack_size",
    "target_audience",
    "use_cases",
    "differentiators",
    "whats_included",
    "category",
    "competitor_refs",
    "backend_keywords",
    "detail_photos",
    "approved_copy",
    "art_direction",
    "locked",
    "locked_by",
    "created_at",
    "updated_at",
    "parent_product_id",
    "variant_label",
    "copy_overrides",
    "updated_by",
    "updated_by_name",
  ],
  programs: [
    "id",
    "name",
    "channel",
    "assignee",
    "priority",
    "deadline",
    "stage",
    "status",
    "product_count",
    "workflow_status",
    "locked",
    "locked_by",
    "created_at",
    "updated_at",
  ],
  generated_content: [
    "id",
    "product_id",
    "content_id",
    "channel",
    "title",
    "bullets",
    "description",
    "keywords",
    "generated_at",
    "submitted_by",
    "reviewed_at",
    "status",
    "created_at",
    "updated_at",
  ],
  qa_queue: [
    "id",
    "product_id",
    "content_id",
    "type",
    "channel",
    "submitted_by",
    "assigned_to",
    "status",
    "notes",
    "created_at",
    "copy_data",
  ],
  product_activity: [
    "id",
    "product_id",
    "user_id",
    "user_name",
    "action",
    "details",
    "created_at",
  ],
};

// ─── JS camelCase ↔ DB snake_case Mapper ─────────────────────
export const toDb = (obj: any, table?: string): any => {
  if (!obj) return obj;

  const mapping: Record<string, string> = {
    itemNumber: "item_number",
    heroImage: "hero_image",
    sellingFeatures: "selling_features",
    programId: "program_id",
    productId: "product_id",
    packSize: "pack_size",
    targetAudience: "target_audience",
    useCases: "use_cases",
    whatsIncluded: "whats_included",
    competitorRefs: "competitor_refs",
    backendKeywords: "backend_keywords",
    detailPhotos: "detail_photos",
    approvedCopy: "approved_copy",
    artDirection: "art_direction",
    lockedBy: "locked_by",
    copyData: "copy_data",
    productCount: "product_count",
    submittedBy: "submitted_by",
    submittedAt: "submitted_at",
    contentId: "content_id",
    generatedAt: "generated_at",
    reviewedAt: "reviewed_at",
    workflowStatus: "workflow_status",
    assignedTo: "assigned_to",
    avatarUrl: "avatar_url",
    parentProductId: "parent_product_id",
    variantLabel: "variant_label",
    copyOverrides: "copy_overrides",
    updatedBy: "updated_by",
    updatedByName: "updated_by_name",
    userId: "user_id",
    userName: "user_name",
  };

  const result = { ...obj };
  Object.entries(mapping).forEach(([js, db]) => {
    if (js in result) {
      result[db] = result[js];
      delete result[js];
    }
  });

  // Strip fields not in the DB schema to prevent "column not found" errors
  if (table) {
    const allowedCols = DB_COLUMNS[table];
    if (allowedCols) {
      Object.keys(result).forEach((k) => {
        if (!allowedCols.includes(k)) delete result[k];
      });
    }
  }

  return result;
};

export const fromDb = (obj: any): any => {
  if (!obj) return obj;

  const mapping: Record<string, string> = {
    item_number: "itemNumber",
    hero_image: "heroImage",
    selling_features: "sellingFeatures",
    program_id: "programId",
    product_id: "productId",
    pack_size: "packSize",
    target_audience: "targetAudience",
    use_cases: "useCases",
    whats_included: "whatsIncluded",
    competitor_refs: "competitorRefs",
    backend_keywords: "backendKeywords",
    detail_photos: "detailPhotos",
    approved_copy: "approvedCopy",
    art_direction: "artDirection",
    locked_by: "lockedBy",
    copy_data: "copyData",
    product_count: "productCount",
    submitted_by: "submittedBy",
    submitted_at: "submittedAt",
    content_id: "contentId",
    generated_at: "generatedAt",
    reviewed_at: "reviewedAt",
    workflow_status: "workflowStatus",
    assigned_to: "assignedTo",
    avatar_url: "avatarUrl",
    parent_product_id: "parentProductId",
    variant_label: "variantLabel",
    copy_overrides: "copyOverrides",
    updated_by: "updatedBy",
    updated_by_name: "updatedByName",
    user_id: "userId",
    user_name: "userName",
  };

  const result = { ...obj };
  Object.entries(mapping).forEach(([db, js]) => {
    if (db in result) {
      result[js] = result[db];
      delete result[db];
    }
  });

  // Ensure array fields are actual arrays (Supabase may return strings)
  const arrayFields = [
    "whatsIncluded", "useCases", "differentiators", "sellingFeatures",
    "competitorRefs", "backendKeywords", "detailPhotos",
  ];
  arrayFields.forEach((field) => {
    if (field in result && !Array.isArray(result[field])) {
      const val = result[field];
      if (typeof val === "string") {
        try {
          const parsed = JSON.parse(val);
          result[field] = Array.isArray(parsed) ? parsed : val ? val.split(",").map((s: string) => s.trim()).filter(Boolean) : [];
        } catch {
          result[field] = val ? val.split(",").map((s: string) => s.trim()).filter(Boolean) : [];
        }
      } else {
        result[field] = [];
      }
    }
  });

  return result;
};

// ─── Timeout Utility ──────────────────────────────────────────
// Prevents app from hanging when Supabase is unreachable
export const withTimeout = (promise: PromiseLike<any>, ms = CONFIG.REQUEST_TIMEOUT): Promise<any> => {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("Request timed out")), ms)),
  ]);
};

// ─── Authenticated Fetch Helper ────────────────────────────────
// Sends Supabase JWT with every API call
export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const session = supabase ? (await supabase.auth.getSession())?.data?.session : null;
  const token = session?.access_token;
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

// ─── Data Persistence Layer (DB Object) ────────────────────────
export const DB = {
  isConnected: () => !!supabase,

  // ─── Products ───
  async loadProducts(): Promise<Product[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(supabase.from("products").select("*").order("id"));
    if (error) console.error("loadProducts error:", error);
    return data ? data.map(fromDb) : null;
  },

  PAGE_SIZE: 100,

  async countProducts(): Promise<number> {
    if (!supabase) return 0;
    try {
      const { count, error } = await withTimeout(
        supabase.from("products").select("id", { count: "exact", head: true })
      );
      if (error) {
        console.error("countProducts error:", error);
        return 0;
      }
      return count || 0;
    } catch (e) {
      return 0;
    }
  },

  async loadProductsPage(page = 0, pageSize = 100): Promise<Product[] | null> {
    if (!supabase) return null;
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data, error } = await withTimeout(
      supabase.from("products").select("*").order("id").range(from, to)
    );
    if (error) console.error("loadProductsPage error:", error);
    return data ? data.map(fromDb) : null;
  },

  async searchProducts(query: string = "", programId: string | null = null, limit = 50): Promise<Product[] | null> {
    if (!supabase) return null;
    let q = supabase.from("products").select("*").order("id");
    if (query) {
      // Sanitize input: escape PostgREST special characters to prevent filter injection
      const sanitized = query.replace(/[%_\\(),."']/g, c => `\\${c}`);
      q = q.or(`title.ilike.%${sanitized}%,item_number.ilike.%${sanitized}%`);
    }
    if (programId) {
      q = q.eq("program_id", programId);
    }
    q = q.limit(limit);
    const { data, error } = await withTimeout(q);
    if (error) console.error("searchProducts error:", error);
    return data ? data.map(fromDb) : null;
  },

  async saveProduct(product: Product): Promise<Product | null> {
    if (!supabase) return null;
    const dbProduct = toDb(product, "products");
    const hasPhotos =
      dbProduct.detail_photos &&
      dbProduct.detail_photos.length > 0 &&
      dbProduct.detail_photos.some(Boolean);
    const timeout = hasPhotos ? CONFIG.PHOTO_TIMEOUT : CONFIG.PRODUCT_TIMEOUT;
    const { data, error } = await withTimeout(
      supabase.from("products").upsert([dbProduct]).select(),
      timeout
    );
    if (error) {
      console.error("saveProduct error:", error);
      // Retry without columns that may be too large
      const stripped = { ...dbProduct };
      if (
        error.message?.includes("detail_photos") ||
        error.message?.includes("payload") ||
        error.code === "PGRST204"
      ) {
        delete stripped.detail_photos;
        warn("Retrying saveProduct without detail_photos...");
        const { data: d2, error: e2 } = await withTimeout(
          supabase.from("products").upsert([stripped]).select(),
          timeout
        );
        if (e2) console.error("saveProduct retry error:", e2);
        return d2 && d2.length ? fromDb(d2[0]) : null;
      }
    }
    return data && data.length ? fromDb(data[0]) : null;
  },

  async deleteProduct(id: string): Promise<void> {
    if (!supabase) return;
    // Delete child rows first to avoid foreign key conflicts
    // Try both string and numeric ID formats since some tables may store product_id differently
    const childTables = ["generated_content", "qa_queue", "product_copy", "product_art_direction", "approved_images"];
    for (const table of childTables) {
      try {
        await withTimeout(supabase.from(table).delete().eq("product_id", id));
      } catch { /* table may not exist */ }
      // Also try numeric ID if the string version didn't match
      if (/^\d+$/.test(id)) {
        try {
          await withTimeout(supabase.from(table).delete().eq("product_id", Number(id)));
        } catch { /* ignore */ }
      }
    }
    const { error } = await withTimeout(supabase.from("products").delete().eq("id", id));
    if (error) console.error("deleteProduct error:", error);
  },

  // ─── Programs ───
  async loadPrograms(): Promise<Program[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(supabase.from("programs").select("*").order("id"));
    if (error) console.error("loadPrograms error:", error);
    return data ? data.map(fromDb) : null;
  },

  async saveProgram(program: Program): Promise<Program | null> {
    if (!supabase) return null;
    const dbProgram = toDb(program, "programs");
    const { data, error } = await withTimeout(
      supabase.from("programs").upsert([dbProgram]).select()
    );
    if (error) console.error("saveProgram error:", error);
    return data && data.length ? fromDb(data[0]) : null;
  },

  async updateProgramStage(id: string, stage: string): Promise<void> {
    if (!supabase) return;
    const { error } = await withTimeout(supabase.from("programs").update({ stage }).eq("id", id));
    if (error) console.error("updateProgramStage error:", error);
  },

  // ─── Generated Content ───
  async loadGeneratedContent(): Promise<GeneratedContent[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(
      supabase.from("generated_content").select("*").order("id")
    );
    if (error) console.error("loadGeneratedContent error:", error);
    return data ? data.map(fromDb) : null;
  },

  async saveGeneratedContent(content: GeneratedContent): Promise<GeneratedContent | null> {
    if (!supabase) return null;
    const dbContent = toDb(content, "generated_content");

    // Strip id & created_at so the DB auto-generates them on insert
    const { id: _id, created_at: _ca, ...insertFields } = dbContent;

    // Check if a row already exists for this product + channel
    const { data: existing } = await withTimeout(
      supabase.from("generated_content").select("id")
        .eq("product_id", dbContent.product_id)
        .eq("channel", dbContent.channel)
        .limit(1)
    );

    if (existing && existing.length > 0) {
      // Update existing row
      const { data: updated, error: upErr } = await withTimeout(
        supabase.from("generated_content").update(insertFields)
          .eq("id", existing[0].id)
          .select()
      );
      if (!upErr && updated && updated.length) return fromDb(updated[0]);
      if (upErr) console.error("[saveGeneratedContent] Update error:", upErr);
      return null;
    }

    // No existing row — insert fresh
    const { data, error } = await withTimeout(
      supabase.from("generated_content").insert([insertFields]).select()
    );
    if (!error && data && data.length) return fromDb(data[0]);
    if (error) console.error("[saveGeneratedContent] Insert error:", error);
    return null;
  },

  async updateGeneratedContentStatus(id: string, status: string): Promise<void> {
    if (!supabase) return;
    const { error } = await withTimeout(
      supabase.from("generated_content").update({ status }).eq("id", id)
    );
    if (error) console.error("updateGeneratedContentStatus error:", error);
  },

  // ─── QA Queue ───
  async loadQAQueue(): Promise<QAItem[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(
      supabase.from("qa_queue").select("*").order("created_at", { ascending: false })
    );
    if (error) console.error("loadQAQueue error:", error);
    return data ? data.map(fromDb) : null;
  },

  async saveQAItem(item: QAItem): Promise<QAItem | null> {
    if (!supabase) return null;
    const dbItem = toDb(item, "qa_queue");

    // Strip id & created_at so the DB auto-generates them on insert
    const { id: _id, created_at: _ca, ...insertFields } = dbItem;

    // Check if a row already exists for this product + channel
    const { data: existing } = await withTimeout(
      supabase.from("qa_queue").select("id")
        .eq("product_id", dbItem.product_id)
        .eq("channel", dbItem.channel)
        .limit(1)
    );

    if (existing && existing.length > 0) {
      // Update existing row
      const { data: updated, error: upErr } = await withTimeout(
        supabase.from("qa_queue").update(insertFields)
          .eq("id", existing[0].id)
          .select()
      );
      if (!upErr && updated && updated.length) return fromDb(updated[0]);
      if (upErr) console.error("[saveQAItem] Update error:", upErr);
      return null;
    }

    // No existing row — insert fresh
    const { data, error } = await withTimeout(
      supabase.from("qa_queue").insert([insertFields]).select()
    );
    if (!error && data && data.length) return fromDb(data[0]);
    if (error) console.error("[saveQAItem] Insert error:", error);
    return null;
  },

  async updateQAAssignee(id: string, assignedTo: string): Promise<void> {
    if (!supabase) return;
    const { error } = await withTimeout(supabase.from("qa_queue").update({ assigned_to: assignedTo || null }).eq("id", id));
    if (error) console.error("updateQAAssignee error:", error);
  },

  async updateQAStatus(id: string, status: string, notes: string | null = null): Promise<void> {
    if (!supabase) return;
    const update: any = { status };
    if (notes !== null) update.notes = notes;
    const { error } = await withTimeout(supabase.from("qa_queue").update(update).eq("id", id));
    if (error) console.error("updateQAStatus error:", error);
  },

  async deleteQAItem(id: string): Promise<void> {
    if (!supabase) return;
    const { error } = await withTimeout(supabase.from("qa_queue").delete().eq("id", id));
    if (error) console.error("deleteQAItem error:", error);
  },

  // ─── Team Members ───
  async loadTeamMembers(): Promise<TeamMember[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(
      supabase.from("team_members").select("*").order("id")
    );
    if (error) console.error("loadTeamMembers error:", error);
    return data ? data.map(fromDb) : null;
  },

  async saveTeamMember(member: TeamMember): Promise<TeamMember | null> {
    if (!supabase) return null;
    // Strip client-only fields before saving
    const { isCurrentUser, programs, products, lastActive, status, initials, ...saveable } = member as any;
    const dbMember = toDb(saveable, "team_members");
    // Try update by email first (most reliable match), fall back to upsert by id
    if (dbMember.email) {
      const { data: existing } = await withTimeout(
        supabase.from("team_members").select("id").eq("email", dbMember.email).limit(1)
      ).catch(() => ({ data: null }));
      if (existing && existing.length > 0) {
        const { id: _, ...updates } = dbMember;
        const { data, error } = await withTimeout(
          supabase.from("team_members").update(updates).eq("email", dbMember.email).select()
        );
        if (error) console.error("saveTeamMember update error:", error);
        return data && data.length ? fromDb(data[0]) : null;
      }
    }
    // New member — strip id (auth UUID won't match integer PK) and insert
    const { id: _skipId, ...insertData } = dbMember;
    const { data, error } = await withTimeout(
      supabase.from("team_members").insert([insertData]).select()
    );
    if (error) console.error("saveTeamMember error:", error);
    return data && data.length ? fromDb(data[0]) : null;
  },

  // ─── Invite Codes ───
  async checkInviteCode(hash: string): Promise<boolean> {
    if (!supabase) return false;
    try {
      const { data, error } = await withTimeout(
        supabase.from("invite_codes").select("id").eq("code_hash", hash).eq("active", true).limit(1)
      );
      if (error) console.error("checkInviteCode error:", error);
      return data && data.length > 0;
    } catch (e) {
      warn("checkInviteCode timed out, falling back to offline auth");
      return false;
    }
  },

  async seedInviteCodes(hashes: string[]): Promise<void> {
    if (!supabase) return;
    try {
      const { count } = await withTimeout(
        supabase.from("invite_codes").select("id", { count: "exact" })
      );
      if (count && count > 0) return;

      const codes = hashes.map((hash, i) => ({
        code_hash: hash,
        label: `Invite Code ${i + 1}`,
        created_by: "system",
        active: true,
      }));
      const { error } = await withTimeout(supabase.from("invite_codes").insert(codes));
      if (error) console.error("seedInviteCodes error:", error);
    } catch (e) {
      warn("seedInviteCodes timed out, skipping");
    }
  },

  // ─── App Settings ───
  async loadSettings(retries = 2): Promise<AppSettings | null> {
    if (!supabase) return null;
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const { data, error } = await withTimeout(
          supabase.from("app_settings").select("*").eq("id", 1).single()
        );
        if (error) {
          warn("loadSettings:", error.message);
          return null;
        }
        return data?.settings || null;
      } catch (e) {
        if (attempt < retries) {
          warn(`loadSettings attempt ${attempt + 1} timed out, retrying...`);
          await new Promise((r) => setTimeout(r, 1000));
        } else {
          warn("loadSettings timed out after retries");
          return null;
        }
      }
    }
    return null;
  },

  async saveSettings(settings: AppSettings): Promise<any | null> {
    if (!supabase) return null;
    try {
      const { data, error } = await withTimeout(
        supabase
          .from("app_settings")
          .upsert([{ id: 1, settings, updated_at: new Date().toISOString() }])
          .select()
      );
      if (error) console.error("saveSettings error:", error);
      return data?.[0] || null;
    } catch (e) {
      warn("saveSettings timed out");
      return null;
    }
  },

  // ─── Approved Images ───
  async loadApprovedImages(productId?: string): Promise<any[]> {
    if (!supabase) return [];
    try {
      let query = supabase.from("approved_images").select("*").order("slot");
      if (productId) query = query.eq("product_id", productId);
      const { data, error } = await withTimeout(query);
      if (error) {
        console.error("loadApprovedImages error:", error);
        return [];
      }
      return data || [];
    } catch (e) {
      warn("loadApprovedImages timed out");
      return [];
    }
  },

  async saveApprovedImage(imageData: {
    productId: string;
    slot: string;
    imageType: string;
    imageUrl: string;
    imageStyle?: string;
    artDirection?: any;
    approvedBy?: string;
  }): Promise<any | null> {
    if (!supabase) return null;
    try {
      const row = {
        product_id: imageData.productId,
        slot: imageData.slot,
        image_type: imageData.imageType,
        image_url: imageData.imageUrl,
        image_style: imageData.imageStyle || "sketch",
        art_direction: imageData.artDirection || null,
        approved_by: imageData.approvedBy || null,
        approved_at: new Date().toISOString(),
      };
      const { data, error } = await withTimeout(
        supabase
          .from("approved_images")
          .upsert([row], { onConflict: "product_id,slot,image_style" })
          .select()
      );
      if (error) {
        console.error("saveApprovedImage error:", error);
        return null;
      }
      return data?.[0] || null;
    } catch (e) {
      warn("saveApprovedImage timed out");
      return null;
    }
  },

  async deleteApprovedImage(id: string): Promise<void> {
    if (!supabase) return;
    try {
      const { error } = await withTimeout(
        supabase.from("approved_images").delete().eq("id", id)
      );
      if (error) console.error("deleteApprovedImage error:", error);
    } catch (e) {
      warn("deleteApprovedImage timed out");
    }
  },

  // ─── Seed Data ───
  async seedData(
    products?: Product[],
    programs?: Program[],
    teamMembers?: TeamMember[]
  ): Promise<void> {
    if (!supabase) return;

    try {
      const { count: pCount } = await withTimeout(
        supabase.from("products").select("id", { count: "exact" })
      );
      if (pCount && pCount > 0) return;
    } catch (e) {
      warn("seedData check timed out, skipping seed");
      return;
    }

    // Insert seed data
    try {
      if (products && products.length > 0) {
        const dbProducts = products.map((p) => toDb(p, "products"));
        const { error } = await withTimeout(supabase.from("products").insert(dbProducts));
        if (error) console.error("seedData products error:", error);
      }

      if (programs && programs.length > 0) {
        const dbPrograms = programs.map((p) => toDb(p, "programs"));
        const { error } = await withTimeout(supabase.from("programs").insert(dbPrograms));
        if (error) console.error("seedData programs error:", error);
      }

      if (teamMembers && teamMembers.length > 0) {
        const dbMembers = teamMembers.map((m) => toDb(m));
        const { error } = await withTimeout(supabase.from("team_members").insert(dbMembers));
        if (error) console.error("seedData teamMembers error:", error);
      }
    } catch (e) {
      warn("seedData insert timed out:", (e as Error).message);
    }
  },

  // ─── Product Copy (Normalized) ───
  async loadProductCopy(productId?: string): Promise<any[] | null> {
    if (!supabase) return null;
    let q = supabase.from("product_copy").select("*");
    if (productId) q = q.eq("product_id", productId);
    const { data, error } = await withTimeout(q);
    if (error) {
      warn("loadProductCopy:", error.message);
      return null;
    }
    return data || [];
  },

  async loadAllProductCopy(): Promise<any[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(supabase.from("product_copy").select("*"));
    if (error) {
      warn("loadAllProductCopy:", error.message);
      return null;
    }
    return data || [];
  },

  async saveProductCopy(
    productId: string,
    channel: string,
    copyData: Record<string, any>
  ): Promise<any | null> {
    if (!supabase) return null;
    const row = {
      product_id: productId,
      channel: channel,
      title: copyData.title || "",
      bullets: copyData.bullets || [],
      description: copyData.description || "",
      backend_keywords: copyData.backendKeywords || "",
      approved_at: copyData.approvedAt || new Date().toISOString(),
      approved_by: copyData.approvedBy || "",
    };
    const { data, error } = await withTimeout(
      supabase.from("product_copy").upsert([row], { onConflict: "product_id,channel" }).select()
    );
    if (error) console.error("saveProductCopy error:", error);
    return data?.[0] || null;
  },

  // ─── Product Art Direction (Normalized) ───
  // Table: product_art_direction
  // Schema: product_id, image_style, images (JSONB briefs), generated_images (JSONB), saved_at
  // Composite key: (product_id, image_style)
  //
  // Stores visual direction briefs and generated image URLs.
  // - image_style="default" holds the briefs (images array)
  // - generated_images is keyed by style: { sketch: { 1: {...}, 2: {...} }, realism: { ... } }

  async loadAllProductArtDirection(): Promise<any[] | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(
      supabase.from("product_art_direction").select("*")
    );
    if (error) {
      warn("loadAllProductArtDirection:", error.message);
      return null;
    }
    return data || [];
  },

  async loadProductArtDirection(productId: string): Promise<any | null> {
    if (!supabase) return null;
    const { data, error } = await withTimeout(
      supabase.from("product_art_direction").select("*").eq("product_id", productId)
    );
    if (error) {
      warn("loadProductArtDirection:", error.message);
      return null;
    }
    return data?.[0] || null;
  },

  async saveProductArtDirection(
    productId: string,
    artData: Record<string, any>,
  ): Promise<any | null> {
    if (!supabase) return null;
    // Strip base64 dataUrl fields — only keep storage URLs to avoid payload bloat
    const cleanGeneratedImages = (gi: Record<string, any>) => {
      const cleaned: Record<string, any> = {};
      for (const [style, slots] of Object.entries(gi || {})) {
        if (typeof slots !== "object" || !slots) continue;
        cleaned[style] = {};
        for (const [slot, img] of Object.entries(slots as Record<string, any>)) {
          if (!img || typeof img !== "object") continue;
          const { dataUrl, ...rest } = img as any;
          cleaned[style][slot] = rest;
        }
      }
      return cleaned;
    };
    const row = {
      product_id: productId,
      image_style: "default",
      images: artData.images || [],
      generated_images: cleanGeneratedImages(artData.generatedImages),
      saved_at: artData.savedAt || new Date().toISOString(),
    };
    const { data, error } = await withTimeout(
      supabase
        .from("product_art_direction")
        .upsert([row], { onConflict: "product_id,image_style" })
        .select()
    );
    if (error) console.error("saveProductArtDirection error:", error);
    return data?.[0] || null;
  },

  // ─── Product Activity Log ───
  async logActivity(
    productId: string,
    action: ActivityAction,
    userId?: string,
    userName?: string,
    details?: Record<string, unknown>,
  ): Promise<void> {
    if (!supabase) return;
    const row = toDb(
      { productId, userId, userName, action, details: details || {} },
      "product_activity",
    );
    const { error } = await supabase.from("product_activity").insert([row]);
    if (error) console.error("logActivity error:", error);
  },

  async getProductActivity(
    productId: string,
    limit = 50,
  ): Promise<ProductActivity[]> {
    if (!supabase) return [];
    const { data, error } = await withTimeout(
      supabase
        .from("product_activity")
        .select("*")
        .eq("product_id", productId)
        .order("created_at", { ascending: false })
        .limit(limit),
    );
    if (error) {
      console.error("getProductActivity error:", error);
      return [];
    }
    return (data || []).map(fromDb) as ProductActivity[];
  },
};
